import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../AuthContext';
import { apiJson } from '../apiClient';
import { translate } from './translations';

const STORAGE_PREFIX = 'stocktrack_lang';

const LanguageContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: (key) => key,
});

function storageKey(userId) {
  return `${STORAGE_PREFIX}:${userId || 'guest'}`;
}

export function readStoredLanguage(userId) {
  try {
    const v = localStorage.getItem(storageKey(userId));
    return v === 'en' || v === 'sw' ? v : null;
  } catch {
    return null;
  }
}

function writeStoredLanguage(userId, lang) {
  try {
    localStorage.setItem(storageKey(userId), lang);
  } catch {
    /* ignore */
  }
}

function applyDocumentLanguage(lang) {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = lang === 'sw' ? 'sw' : 'en';
}

export function LanguageProvider({ children }) {
  const { user, refreshMe } = useAuth();
  const userId = user?.id || 'guest';
  const [lang, setLangState] = useState(() => {
    const fromUser = user?.preferences?.language;
    if (fromUser === 'en' || fromUser === 'sw') return fromUser;
    return readStoredLanguage(userId) || 'en';
  });

  useEffect(() => {
    const fromUser = user?.preferences?.language;
    if (fromUser === 'en' || fromUser === 'sw') {
      setLangState(fromUser);
      writeStoredLanguage(userId, fromUser);
    } else {
      const stored = readStoredLanguage(userId);
      if (stored) setLangState(stored);
    }
  }, [user?.preferences?.language, userId]);

  useEffect(() => {
    applyDocumentLanguage(lang);
  }, [lang]);

  const setLang = useCallback(
    async (next) => {
      if (next !== 'en' && next !== 'sw') return;
      setLangState(next);
      writeStoredLanguage(userId, next);
      applyDocumentLanguage(next);
      if (!user?.id) return;
      try {
        await apiJson('/me/preferences', {
          method: 'PATCH',
          body: JSON.stringify({ language: next }),
        });
        await refreshMe();
      } catch {
        /* local language still applied */
      }
    },
    [user?.id, userId, refreshMe]
  );

  const t = useCallback((key, vars) => translate(lang, key, vars), [lang]);

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
}
